import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert
} from '@mui/material';
import {
  CloudQueue,
} from '@mui/icons-material';

const VMProvisioningForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    provider: '',
    region: '',
    vmName: '',
    vmSize: '',
    osType: '',
    diskSize: '',
    resourceGroup: '',
    vnet: '',
    subnet: '',
    publicIp: false,
    tags: {}
  });

  const steps = ['Cloud Provider', 'VM Configuration', 'Network Settings', 'Review & Submit'];

  const azureRegions = [
    'East US', 'West US', 'Central US', 'North Europe', 'West Europe', 'Southeast Asia'
  ];

  const awsRegions = [
    'us-east-1', 'us-west-2', 'eu-west-1', 'ap-southeast-1', 'ap-northeast-1'
  ];

  const vmSizes = {
    azure: ['Standard_B1s', 'Standard_B2s', 'Standard_D2s_v3', 'Standard_D4s_v3'],
    aws: ['t3.micro', 't3.small', 't3.medium', 'm5.large', 'm5.xlarge']
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Select Cloud Provider
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: formData.provider === 'azure' ? 2 : 1,
                  borderColor: formData.provider === 'azure' ? '#0078d4' : 'divider',
                  '&:hover': { borderColor: '#0078d4' }
                }}
                onClick={() => handleInputChange('provider', 'azure')}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <CloudQueue sx={{ fontSize: 48, color: '#0078d4', mb: 2 }} />
                  <Typography variant="h6">Microsoft Azure</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Enterprise cloud platform
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: formData.provider === 'aws' ? 2 : 1,
                  borderColor: formData.provider === 'aws' ? '#ff9900' : 'divider',
                  '&:hover': { borderColor: '#ff9900' }
                }}
                onClick={() => handleInputChange('provider', 'aws')}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <CloudQueue sx={{ fontSize: 48, color: '#ff9900', mb: 2 }} />
                  <Typography variant="h6">Amazon AWS</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Scalable cloud services
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {formData.provider && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={formData.region}
                    label="Region"
                    onChange={(e) => handleInputChange('region', e.target.value)}
                  >
                    {(formData.provider === 'azure' ? azureRegions : awsRegions).map(region => (
                      <MenuItem key={region} value={region}>{region}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Virtual Machine Configuration
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="VM Name"
                value={formData.vmName}
                onChange={(e) => handleInputChange('vmName', e.target.value)}
                placeholder="Enter VM name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>VM Size</InputLabel>
                <Select
                  value={formData.vmSize}
                  label="VM Size"
                  onChange={(e) => handleInputChange('vmSize', e.target.value)}
                >
                  {(vmSizes[formData.provider] || []).map(size => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Operating System</InputLabel>
                <Select
                  value={formData.osType}
                  label="Operating System"
                  onChange={(e) => handleInputChange('osType', e.target.value)}
                >
                  <MenuItem value="windows">Windows Server 2019</MenuItem>
                  <MenuItem value="ubuntu">Ubuntu 20.04 LTS</MenuItem>
                  <MenuItem value="centos">CentOS 8</MenuItem>
                  <MenuItem value="rhel">Red Hat Enterprise Linux</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Disk Size (GB)"
                type="number"
                value={formData.diskSize}
                onChange={(e) => handleInputChange('diskSize', e.target.value)}
                placeholder="128"
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Network Configuration
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Resource Group"
                value={formData.resourceGroup}
                onChange={(e) => handleInputChange('resourceGroup', e.target.value)}
                placeholder="rg-production"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Virtual Network"
                value={formData.vnet}
                onChange={(e) => handleInputChange('vnet', e.target.value)}
                placeholder="vnet-main"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Subnet"
                value={formData.subnet}
                onChange={(e) => handleInputChange('subnet', e.target.value)}
                placeholder="subnet-web"
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review Configuration
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please review your configuration before submitting the request.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Provider</Typography>
                      <Typography variant="body1">{formData.provider?.toUpperCase()}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Region</Typography>
                      <Typography variant="body1">{formData.region}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">VM Name</Typography>
                      <Typography variant="body1">{formData.vmName}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">VM Size</Typography>
                      <Typography variant="body1">{formData.vmSize}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Operating System</Typography>
                      <Typography variant="body1">{formData.osType}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Disk Size</Typography>
                      <Typography variant="body1">{formData.diskSize} GB</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Provision Virtual Machine
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          
          <Button
            variant="contained"
            onClick={activeStep === steps.length - 1 ? () => alert('VM provisioning request submitted!') : handleNext}
            sx={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
              }
            }}
          >
            {activeStep === steps.length - 1 ? 'Submit Request' : 'Next'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VMProvisioningForm;
